/**
 * Single product: lightbox galeri, accordion Details, dan sticky CTA.
 */

import GLightbox from "glightbox";
import Accordion from "accordion-js";
import { Splide } from "@splidejs/splide";

var VIEWED_KEY = "dufeng_recently_viewed";
var VIEWED_MAX = 20;

export function initSingleProduct() {
  initProductMediaGallery();
  initProductMediaLightbox();
  initVariationFeaturedImage();
  initProductAccordions();
  initRelatedSlider();
  initProductTabs();
  initStickyCta();
  initShareToggle();
  initGiftbox();
}

/**
 * Riwayat produk disimpan di localStorage (bukan cookie) supaya tidak
 * terpengaruh full-page cache: halaman yang di-cache tetap identik untuk semua
 * pengunjung, riwayat dibaca setelah halaman dirender.
 */
function readViewedIds() {
  try {
    var raw = window.localStorage.getItem(VIEWED_KEY);
    if (!raw) return [];
    var ids = JSON.parse(raw);
    return Array.isArray(ids) ? ids.filter(function (id) { return parseInt(id, 10) > 0; }) : [];
  } catch (e) {
    return [];
  }
}

function rememberViewedId(productId) {
  if (!productId) return;

  try {
    var ids = readViewedIds().filter(function (id) {
      return parseInt(id, 10) !== parseInt(productId, 10);
    });
    ids.unshift(parseInt(productId, 10));
    window.localStorage.setItem(VIEWED_KEY, JSON.stringify(ids.slice(0, VIEWED_MAX)));
  } catch (e) {
    /* localStorage penuh atau diblokir: riwayat dilewati saja */
  }
}

function initProductTabs() {
  var section = document.querySelector(".dufeng-product-tabs");
  if (!section) return;

  var productId = section.dataset.productId;
  var buttons = Array.prototype.slice.call(section.querySelectorAll(".dufeng-tabs__btn"));
  var panels = Array.prototype.slice.call(section.querySelectorAll(".dufeng-tabs__panel"));

  // Riwayat dibaca dulu, baru produk ini dicatat, supaya tidak ikut daftar sendiri.
  var viewedIds = readViewedIds();
  rememberViewedId(productId);

  buttons.forEach(function (button) {
    button.addEventListener("click", function () {
      if (button.classList.contains("is-active")) return;

      var target = button.dataset.tab;

      buttons.forEach(function (b) {
        var active = b === button;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-selected", active ? "true" : "false");
      });

      panels.forEach(function (panel) {
        panel.classList.toggle("is-active", panel.dataset.panel === target);
      });

      if ("recent" === target) {
        loadRecentlyViewed(section, viewedIds, productId);
      }
    });
  });
}

function loadRecentlyViewed(section, viewedIds, productId) {
  var panel = section.querySelector('.dufeng-tabs__panel[data-panel="recent"]');
  if (!panel || "1" === panel.dataset.loaded) return;

  panel.dataset.loaded = "1";
  panel.classList.add("is-loading");

  var body = new FormData();
  body.append("action", "dufeng_recently_viewed");
  body.append("nonce", serawai_ajax.nonce);
  body.append("product_id", productId || "");
  body.append("viewed", viewedIds.join(","));
  body.append("exclude", section.dataset.exclude || "");

  fetch(serawai_ajax.url, { method: "POST", body: body })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      if (data && data.success && data.data.html) {
        panel.querySelector(".ss-row").innerHTML = data.data.html;
      } else {
        panel.dataset.loaded = "0";
      }
    })
    .catch(function () {
      panel.dataset.loaded = "0";
    })
    .finally(function () {
      panel.classList.remove("is-loading");
    });
}

/**
 * Galeri gambar produk: grid statis di desktop, slider Splide di tablet &
 * mobile — 1 gambar per slide (peek slide berikutnya) di mobile, 2 gambar
 * per slide (peek gambar ketiga) di tablet, tanpa pagination/arrows. Splide
 * di-mount/destroy mengikuti breakpoint 991px supaya markup grid desktop
 * (lihat _single-product.scss) tidak pernah diambil alih oleh instance Splide.
 */
function initProductMediaGallery() {
  var element = document.querySelector(".dufeng-product-media.splide");
  if (!element) return;

  var slideCount = element.querySelectorAll(".splide__slide").length;
  if (slideCount < 2) return;

  var mql = window.matchMedia("(max-width: 991px)");
  var splide = null;

  function sync(isCompact) {
    if (isCompact && !splide) {
      splide = new Splide(element, {
        type: "slide",
        perPage: 2,
        perMove: 1,
        gap: "8px",
        padding: "15px",
        arrows: false,
        pagination: false,
        pauseOnHover: false,
        breakpoints: {
          767: {
            autoWidth: true,
            perPage: 1,
            gap: "15px",
          },
        },
      });
      splide.mount();
    } else if (!isCompact && splide) {
      splide.destroy();
      splide = null;
    }
  }

  sync(mql.matches);
  mql.addEventListener("change", function (e) {
    sync(e.matches);
  });
}

function initProductMediaLightbox() {
  var mediaLinks = document.querySelectorAll(".dufeng-product-media__item.glightbox");
  if (!mediaLinks.length) {
    return;
  }

  GLightbox({
    selector: ".dufeng-product-media__item.glightbox",
    touchNavigation: true,
    loop: true,
    zoomable: true,
    width: "90vw",
    height: "90vh",
  });
}

/**
 * Saat user memilih variant (mis. warna) yang punya "Variation Image" sendiri,
 * ganti gambar utama (.is-featured) sesuai variant itu — pakai event
 * found_variation/reset_data bawaan WooCommerce di .variations_form, jadi
 * kompatibel dengan plugin swatch apa pun (mis. Variation Swatches for
 * WooCommerce / vi-wpvs) selama plugin itu tetap memakai <select> asli
 * WooCommerce di baliknya. Markup galeri di sini custom (bukan
 * .woocommerce-product-gallery bawaan WooCommerce), jadi swap gambar bawaan
 * WooCommerce tidak pernah jalan di sini & harus ditangani manual.
 */
function initVariationFeaturedImage() {
  var $ = window.jQuery;
  var form = document.querySelector(".variations_form");
  var featured = document.querySelector(".dufeng-product-media__item.is-featured");
  var img = featured && featured.querySelector(".dufeng-product-media__img");
  if (!$ || !form || !featured || !img) return;

  // Gambar produk asli (index 0), disimpan sebelum variant apa pun dipilih,
  // supaya bisa dikembalikan saat variant di-reset/clear.
  var original = {
    href: featured.getAttribute("href"),
    src: img.getAttribute("src"),
    srcset: img.getAttribute("srcset"),
    sizes: img.getAttribute("sizes"),
    alt: img.getAttribute("alt"),
  };

  function applyImage(image, fallback) {
    if ("A" === featured.tagName) {
      featured.setAttribute("href", image.full_src || fallback.href);
    }
    img.setAttribute("src", image.src || fallback.src);

    if (image.srcset) {
      img.setAttribute("srcset", image.srcset);
    } else {
      img.removeAttribute("srcset");
    }

    if (image.sizes) {
      img.setAttribute("sizes", image.sizes);
    } else {
      img.removeAttribute("sizes");
    }

    img.setAttribute("alt", image.alt || fallback.alt);
  }

  function resetImage() {
    applyImage(original, original);
  }

  $(form).on("found_variation", function (event, variation) {
    // Variant tanpa "Variation Image" sendiri: tetap tampilkan gambar produk.
    if (variation && variation.image && variation.image.src) {
      applyImage(variation.image, original);
    } else {
      resetImage();
    }
  });

  $(form).on("reset_data", resetImage);
}

function initProductAccordions() {
  // Details di kolom kanan: panel pertama terbuka.
  document.querySelectorAll(".dufeng-product-details").forEach(function (container) {
    new Accordion(container, {
      duration: 300,
      showMultiple: true,
      openOnInit: [0],
    });
  });

  // FAQ & Shipping di bawah gambar: semua tertutup.
  document.querySelectorAll(".dufeng-product-info").forEach(function (container) {
    new Accordion(container, {
      duration: 300,
      showMultiple: true,
    });
  });
}

function initRelatedSlider() {
  var sliders = document.querySelectorAll(".dufeng-related-slider");

  sliders.forEach(function (element) {
    new Splide(element, {
      type: "slide",
      perPage: 3,
      perMove: 3,
      // rewind: geser balik ke ujung lain, supaya arrow tidak pernah disabled
      rewind: true,
      gap: "16px",
      arrows: true,
      pagination: false,
      breakpoints: {
        575: {
          perPage: 2,
          perMove: 2,
          gap: "12px",
        },
      },
    }).mount();
  });
}

function initShareToggle() {
  var wrapper = document.querySelector(".dufeng-product-share");
  var toggle = wrapper && wrapper.querySelector(".dufeng-share-label");
  if (!wrapper || !toggle) return;

  toggle.addEventListener("click", function () {
    var isOpen = wrapper.classList.toggle("is-open");
    toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
  });
}

/**
 * Checkbox "gift box" men-toggle panel field WooCommerce Product Add-ons asli
 * (dirender server-side lewat dufeng_render_product_giftbox()). Saat
 * dicentang, panel dibuka; saat dilepas, field di dalamnya direset ke kosong
 * supaya harga add-on tidak ikut kebawa kalau user berubah pikiran.
 */
/**
 * Delegated di document (bukan bind langsung ke elemen): gift box juga
 * dirender ulang di dalam modal quick add-to-cart (.lt-wc-atc-popup) yang
 * di-inject ke DOM belakangan lewat AJAX setelah initSingleProduct() jalan,
 * jadi bind langsung tidak akan pernah kena elemen di dalam modal itu.
 */
function initGiftbox() {
  document.addEventListener("change", function (event) {
    var input = event.target.closest(".dufeng-giftbox__input");
    if (!input) return;

    var wrapper = input.closest(".dufeng-product-giftbox");
    var options = wrapper && wrapper.querySelector(".dufeng-giftbox__options");
    if (!wrapper || !options) return;

    wrapper.classList.toggle("is-open", input.checked);

    if (!input.checked) {
      options.querySelectorAll("select, input, textarea").forEach(function (field) {
        if ("checkbox" === field.type || "radio" === field.type) {
          field.checked = false;
        } else {
          field.value = "";
        }
        field.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
  });
}

function initStickyCta() {
  var sidebar = document.querySelector(".dufeng-product-sidebar");
  var cta = document.querySelector(".dufeng-product-cta");
  if (!sidebar || !cta) {
    return;
  }

  var updateSticky = function () {
    if (window.innerWidth < 992) {
      cta.style.position = "";
      cta.style.top = "";
      return;
    }

    var ctaHeight = cta.offsetHeight;
    var viewportHeight = window.innerHeight;
    var topSpacing = 24;

    if (ctaHeight + topSpacing * 2 < viewportHeight) {
      cta.style.position = "sticky";
      cta.style.top = topSpacing + "px";
    } else {
      cta.style.position = "";
      cta.style.top = "";
    }
  };

  updateSticky();
  window.addEventListener("resize", updateSticky);
}
